/**
 * A2FLipSyncDriver.js (FULL UPDATED)
 *
 * Fix includes:
 *  - enqueueFrames(frames, {autoStart:false}) to buffer frames without starting playback
 *  - syncClock(streamStartMs, wallNowMs) to lock driver time to audio start time
 *  - startPlaybackNow() to start ticking exactly when audio starts
 *
 * Task #27 (debug recorder render-loop sampling) — observation only,
 * gated on `recorder.isRecording` and wrapped in try/catch.
 *
 * Render samples populated from `_tick`:
 *   renderSampleIndex, sampledAtMonoMs, audioContextCurrentTime,
 *   playbackStartTime, currentAudioTimeMs, targetFrameTimeMs,
 *   appliedFrameTimeMs, frameErrorMs, previousFrameTimeMs,
 *   nextFrameTimeMs, interpolationAlpha, frameQueueLength,
 *   earliestFrameInQueueMs, latestFrameInQueueMs, frameQueueLeadMs,
 *   droppedFrameCount, underflowCount, reusedOldFrame,
 *   appliedFutureFrame, appliedLateFrame, smoothingTauMs,
 *   rawBlendshapeValues, smoothedBlendshapeValues.
 *
 * Warnings the driver may fire (deduped via recorder):
 *   FRAME_ERROR_OVER_50, FRAME_ERROR_OVER_100,
 *   SUSTAINED_FRAME_ERROR_OVER_100, BROWSER_RENDER_UNDERFLOW,
 *   SMOOTHING_MAY_BE_TOO_SLOW.
 *   (A2F_CHUNK_PROCESSING_TOO_SLOW is fired from the recorder's
 *   finalize because it depends on bridge-chunk shape.)
 */

// Spec lipsync shapes whose raw + smoothed values are captured per
// render sample. Bridge frame names may be cased differently across
// rigs; we lowercase for lookup and emit the canonical camelCase name.
const _A2F_DEBUG_TRACKED_SHAPES = new Set([
  "jawopen",
  "mouthfunnel",
  "mouthpucker",
  "mouthclose",
  "mouthsmileleft",
  "mouthsmileright",
]);
const _A2F_DEBUG_SHAPE_CANON = {
  jawopen: "jawOpen",
  mouthfunnel: "mouthFunnel",
  mouthpucker: "mouthPucker",
  mouthclose: "mouthClose",
  mouthsmileleft: "mouthSmileLeft",
  mouthsmileright: "mouthSmileRight",
};

// Throttle target — 250 ms ⇒ ~4 Hz, ~40 entries per 10 s reply.
const _A2F_DEBUG_RENDER_SAMPLE_INTERVAL_MS = 250;

// jawOpen "open" threshold for zero-crossing detection in the
// smoothing-lag heuristic. 0.05 keeps noise out while still
// catching real syllable boundaries.
const _A2F_DEBUG_JAW_THRESHOLD = 0.05;

// Window over which we consider a sustained-over-100 condition met.
const _A2F_DEBUG_SUSTAINED_ERR_WINDOW_MS = 500;

// Rolling buffer length for avgAbsErr / smoothing-lag detection.
// At 250 ms throttle this covers ~1 s.
const _A2F_DEBUG_ROLLING_WINDOW = 4;

export class A2FLipSyncDriver {
  constructor(opts) {
    opts = opts || {};
    this.blendShapeControls = opts.blendShapeControls || null; // { name: {mesh,index,value} }
    this.debug = opts.debug !== false;

    this._frameQueue = [];
    // Task #99 (B): timecode-scheduled emotion keyframes. Each entry is
    // { time_ms, emotions } on the SAME normalized "frame-0 == 0" timeline
    // as `_frameQueue`. Instead of applying the latest-received vector on
    // the wall clock (which made expressions drift ahead of the words by
    // the prepare-then-play lead), `_tick` fires each keyframe through the
    // emotion listeners exactly when the audio cursor reaches its timecode.
    this._emotionQueue = [];
    this._lastFiredEmotionTimeMs = -Infinity;
    this._playing = false;
    this._animFrameId = null;
    this._startTimerId = null;
    this._sessionEnded = false;

    this._clockOriginWall = 0;
    this._clockOriginStream = 0;

    // Task #11 (Pause-on-underrun): when the scheduler pauses audio
    // because the frame queue starves, it calls `pauseClock()` here
    // so the driver's stream clock STOPS advancing for the duration
    // of the pause. `resumeClock()` then shifts `_clockOriginWall`
    // forward by the elapsed pause so the next tick's
    // `_getStreamClock` returns the SAME value it returned just
    // before the pause began (modulo the rAF interval). This avoids
    // a parallel clock and never re-anchors the syncClock origin —
    // the once-per-turn syncClock contract is preserved.
    this._clockPaused = false;
    this._clockPausedAtWall = 0;

    // Audio-clock cursor (the A2F-timecode source of truth). When the
    // engine injects a getter via `setAudioClock()`, `_getStreamClock()`
    // reads the playhead straight from the AudioContext that is playing
    // the speaker audio — the SAME 16 kHz PCM byte-timeline the bridge
    // used to stamp every frame's `time_ms`. That makes "show frame N
    // exactly when sample N is heard" exact by construction: zero
    // wall-clock drift over long replies, and automatic self-healing
    // across background-tab rAF throttling (the cursor stalls with the
    // audio instead of running ahead). The getter must return the
    // normalized stream-ms (frame-0 == 0) while audio is playing, or a
    // non-finite value / null during the pre-audio lead-in. When it is
    // never set, the driver behaves exactly as before (wall clock).
    this._audioClockFn = null;

    this._jitterMs = opts.jitterMs === undefined ? 33 : opts.jitterMs;
    this._baseJitterMs = opts.jitterMs === undefined ? 100 : opts.jitterMs;
    // Representative smoothing tau, kept for the lag-vs-tau diagnostics.
    this._smoothTau = opts.smoothTau === undefined ? 40 : opts.smoothTau; // ms
    // Task #99 (A): ASYMMETRIC low-pass on top of the linear frame interp.
    // The linear interpolation between adjacent frames already smooths
    // motion, so a heavy symmetric low-pass on top of it just mutes the
    // fast jaw/lip transitions that carry consonants. Split it:
    //   - fast ATTACK (target rising toward more-open) → crisp syllable
    //     onsets, consonants survive.
    //   - slower RELEASE (target falling toward closed) → no chatter on
    //     the way back to rest.
    // Both default well below the old single 40 ms so the filter softens
    // overall while staying frame-rate independent (beta from dt/tau).
    this._smoothAttackTauMs =
      opts.smoothAttackTauMs === undefined ? 12 : opts.smoothAttackTauMs;
    this._smoothReleaseTauMs =
      opts.smoothReleaseTauMs === undefined ? 45 : opts.smoothReleaseTauMs;
    this._pruneMarginMs = 500;
    this._jitterRampDurationMs = 500;
    this._jitterRampStartWall = 0;

    this._currentValues = {};
    this._nameMap = {};
    this._resolveCache = {};
    this._mirrorMap = {};

    this._lastTickTime = 0;
    this._boundTick = this._tick.bind(this);
    this._didAutoTest = false;

    // ---- Emotion vector plumbing (no morph-target consumption here) ----
    // The bridge attaches a 10-value Audio2Emotion aggregate to each
    // a2f_frames batch (and emits a standalone a2f_emotions message). We
    // cache the most recent vector here so downstream UIs (live emotion
    // display, intensity slider, etc.) can read it without subscribing to
    // the bridge directly. The driver itself does NOT use these values to
    // animate — lipsync is driven by blendshapes alone. Listeners
    // registered via onEmotions(cb) are invoked on every update.
    this.lastEmotions = null;
    this.lastEmotionTimeCode = null;
    this._emotionListeners = [];

    // Post-tick listeners run AFTER `_applyInterpolated` has written
    // morphTargetInfluences for the current frame. The A2F emotion
    // renderer hooks here so its emotion shapes overlay the lipsync
    // values without racing on the same morphTargetInfluences entry.
    this._postTickListeners = [];

    // Task #20: underflow listeners fire ONCE on the rising edge of a
    // queue underrun (queue empty OR past-last-frame, with session still
    // open) — i.e. the instant the driver knows it cannot produce a
    // fresh frame. The starvation gate hooks here so it can pause audio
    // on the same rAF the underflow is detected, instead of waiting for
    // its 30 Hz throttle. `_underflowLatched` is the rising-edge guard;
    // cleared whenever the driver successfully interpolates a real
    // frame from the queue.
    this._underflowListeners = [];
    this._underflowLatched = false;
    // Task #99 (accounting): always-on count of late-frame events this
    // turn (queue dry OR past-last-frame while the session is open). The
    // engine reads this at teardown to attribute "mouth skipped" to the
    // bridge falling behind — distinct from audio being dropped. Reset on
    // each fresh playback start. Read via getUnderflowCount().
    this._underflowCount = 0;

    // Per-instance "is this driver currently writing frames?" gate. Used
    // by `A2FLipSyncDriver.isAnyActive()` so the local idle controllers
    // (BlinkController, EyeTrackingController) can stand down while A2F
    // is animating the eyes and resume between turns. Stamped on every
    // call to `_applyInterpolated`, the single chokepoint that touches
    // morphTargetInfluences. We deliberately do NOT use the `_playing`
    // boolean: it flips false the moment the queue drains, before
    // `_decayToZero` finishes — the timestamp gives a smoother handoff
    // window.
    this._lastFrameApplyWall = 0;
    A2FLipSyncDriver._activeDrivers.add(this);

    // ---- Task #27 debug-recorder hooks ----
    // The recorder + snapshot-fn are injected by the wiring layer
    // (RealtimeLLMToElevenLabs) right after the driver is constructed.
    // Until then, all sampling/warning paths short-circuit on the
    // null check. The snapshot fn is the only way the driver can see
    // the master audio clock + AudioContext / playback start time —
    // those live on RealtimeLLMToElevenLabs and must not be referenced
    // from here directly. Leaving them null in tests / standalone use
    // is safe; render samples simply do not get recorded.
    this._a2fDebugRecorder = null;
    this._a2fDebugSnapshotFn = null;

    // Per-utterance state — reset by `_a2fDebugBeginRenderUtterance`
    // (called from RealtimeLLMToElevenLabs._a2fDebugBeginUtterance).
    // Pre-init to safe defaults so a flag-flip mid-session can never
    // see uninitialized fields.
    this._a2fDebugRenderSampleIndex = 0;
    this._a2fDebugLastRenderSampleAtMonoMs = 0;
    this._a2fDebugDroppedFrameCount = 0;
    this._a2fDebugUnderflowCount = 0;
    this._a2fDebugWasInUnderflow = false;
    this._a2fDebugSustainedErrStartMs = 0; // 0 = not currently sustained
    this._a2fDebugRollingErrs = []; // {atMs, absErr}
    this._a2fDebugLastRawJawCrossingMs = 0;
    this._a2fDebugLastSmoothedJawCrossingMs = 0;
    this._a2fDebugPrevRawJawAbove = false;
    this._a2fDebugPrevSmoothedJawAbove = false;
    // Per-tick scratch — overwritten on every `_applyInterpolated`
    // call when recording is on. Holds the raw + smoothed values for
    // the spec's six lipsync shapes.
    this._a2fDebugLastAppliedSnapshot = null;

    this._buildNameMap();

    if (this.debug) {
      const names = this.blendShapeControls
        ? Object.keys(this.blendShapeControls)
        : [];
      console.log(
        "[A2FLipSync] Driver initialized with",
        names.length,
        "blendshapes",
      );
    }

    // Auto-test only if you want it (kept same behavior)
    if (
      this.blendShapeControls &&
      Object.keys(this.blendShapeControls).length > 0
    ) {
      this._didAutoTest = true;
      setTimeout(() => {
        if (!this._playing) this._autoTestJawOpen();
      }, 500);
    }
  }

  _buildNameMap() {
    this._nameMap = {};
    this._resolveCache = {};
    this._mirrorMap = {};
    if (!this.blendShapeControls) return;

    const keys = Object.keys(this.blendShapeControls);
    const prefixGroups = {};

    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];

      this._nameMap[key.toLowerCase()] = key;

      const dotIdx = key.lastIndexOf(".");
      if (dotIdx !== -1) {
        const stripped = key.substring(dotIdx + 1);
        this._nameMap[stripped.toLowerCase()] = key;

        const prefix = key.substring(0, dotIdx).toLowerCase();
        const suffixLower = stripped.toLowerCase();
        if (!prefixGroups[suffixLower]) prefixGroups[suffixLower] = [];
        prefixGroups[suffixLower].push(key);
      }

      const usIdx = key.lastIndexOf("_");
      if (usIdx !== -1) {
        const suffix = key.substring(usIdx + 1);
        this._nameMap[suffix.toLowerCase()] = key;
      }
    }

    // Mirror map: if same suffix exists under multiple prefixes (Blendshape1.xxx and Blendshape2.xxx)
    for (const suffix in prefixGroups) {
      const group = prefixGroups[suffix];
      if (group.length > 1) {
        for (let i = 0; i < group.length; i++) {
          const mirrors = [];
          for (let j = 0; j < group.length; j++) {
            if (i !== j) mirrors.push(group[j]);
          }
          this._mirrorMap[group[i]] = mirrors;
        }
      }
    }

    if (this.debug) {
      const mirroredCount = Object.keys(this._mirrorMap).length;
      if (mirroredCount > 0) {
        console.log(
          `[A2FLipSync] Mirror map built: ${mirroredCount} shapes have paired variants (Blendshape1/Blendshape2)`,
        );
      }
    }
  }

  _resolve(name) {
    const raw = String(name);
    const cached = this._resolveCache[raw];
    if (cached !== undefined) return cached;

    let resolved = null;

    if (this.blendShapeControls && this.blendShapeControls[raw]) {
      resolved = raw;
    } else {
      const lower = raw.toLowerCase();

      resolved = this._nameMap[lower] || null;

      if (!resolved) {
        const dotIdx = raw.lastIndexOf(".");
        if (dotIdx !== -1) {
          const suffix = raw.substring(dotIdx + 1);
          resolved = this._nameMap[suffix.toLowerCase()] || null;
        }
      }

      if (!resolved) {
        const usIdx = raw.lastIndexOf("_");
        if (usIdx !== -1) {
          const suffix = raw.substring(usIdx + 1);
          resolved = this._nameMap[suffix.toLowerCase()] || null;
        }
      }
    }

    this._resolveCache[raw] = resolved;
    return resolved;
  }

  _applyCtl(ctl, value) {
    if (ctl && ctl.mesh && typeof ctl.index === "number") {
      ctl.mesh.morphTargetInfluences[ctl.index] = value;
      ctl.value = value;
    }
  }

  _applyControl(entry, value) {
    if (Array.isArray(entry)) {
      for (const c of entry) this._applyCtl(c, value);
    } else {
      this._applyCtl(entry, value);
    }
  }

  _setControlValue(key, value) {
    if (!this.blendShapeControls) return;

    this._applyControl(this.blendShapeControls[key], value);

    const mirrors = this._mirrorMap[key];
    if (mirrors) {
      for (let i = 0; i < mirrors.length; i++) {
        this._applyControl(this.blendShapeControls[mirrors[i]], value);
      }
    }
  }

  /**
   * Update the cached emotion vector and notify any registered listeners.
   * Called by the live wiring code when an `a2f_frames` batch carries an
   * `emotions` field, or when a standalone `a2f_emotions` message arrives.
   *
   * @param {object} emotions - { amazement, anger, cheekiness, disgust,
   *                              fear, grief, joy, outOfBreath, pain,
   *                              sadness } in the canonical A2E order.
   * @param {number|null} timeCodeMs - Timecode this vector corresponds to,
   *                              in MILLISECONDS on the SAME normalized
   *                              "frame-0 == 0" timeline as queued frames'
   *                              `time_ms` (the caller is responsible for the
   *                              seconds→ms conversion and the origin shift).
   *                              When finite, the vector is BUFFERED and fired
   *                              by `_tick` when the audio cursor reaches it
   *                              (Task #99 B). When null/non-finite (manual or
   *                              debug paths with no timeline origin) it fires
   *                              immediately, preserving the legacy behavior.
   */
  setEmotions(emotions, timeCodeMs = null) {
    if (!emotions || typeof emotions !== "object") return;
    this.lastEmotions = emotions;
    this.lastEmotionTimeCode = timeCodeMs;

    if (typeof timeCodeMs === "number" && Number.isFinite(timeCodeMs)) {
      // Buffer as a keyframe; `_dispatchScheduledEmotions(renderT)` fires it
      // when the audio cursor reaches its timecode. Keep the queue sorted
      // ascending — keyframes normally arrive in order, so this is an
      // insertion at (or near) the tail.
      const kf = { time_ms: timeCodeMs, emotions };
      const q = this._emotionQueue;
      if (q.length === 0 || timeCodeMs >= q[q.length - 1].time_ms) {
        q.push(kf);
      } else {
        let i = q.length - 1;
        while (i >= 0 && q[i].time_ms > timeCodeMs) i--;
        q.splice(i + 1, 0, kf);
      }
      return;
    }

    // No timeline origin — fire immediately (legacy / manual / debug).
    this._fireEmotionListeners(emotions, timeCodeMs);
  }

  /** Fire all registered onEmotions listeners. Listeners cannot crash us. */
  _fireEmotionListeners(emotions, timeCode) {
    if (this._emotionListeners.length === 0) return;
    for (let i = 0; i < this._emotionListeners.length; i++) {
      try {
        this._emotionListeners[i](emotions, timeCode);
      } catch (_) {
        // Listeners must not crash the driver.
      }
    }
  }

  /**
   * Task #99 (B): fire the emotion keyframe(s) whose timecode the audio
   * cursor has just reached. Called once per `_tick` with the SAME `renderT`
   * the lips render at, so the expression overlay and the mouth shapes move
   * together against the audio. Drains every keyframe at or before the
   * cursor and fires only the latest (the target replaces, not stacks), so a
   * cursor jump never plays a stale intermediate expression.
   */
  _dispatchScheduledEmotions(renderT) {
    const q = this._emotionQueue;
    if (q.length === 0) return;
    let fired = null;
    while (q.length > 0 && q[0].time_ms <= renderT) {
      fired = q.shift();
    }
    if (fired) {
      this._lastFiredEmotionTimeMs = fired.time_ms;
      this._fireEmotionListeners(fired.emotions, fired.time_ms);
    }
  }

  /**
   * Register a callback fired on every emotion-vector update. Returns an
   * `off` function for symmetric unsubscribe.
   *
   *   const off = driver.onEmotions((vec, tc) => console.log(vec));
   *   off();
   */
  onEmotions(cb) {
    if (typeof cb !== "function") return () => {};
    this._emotionListeners.push(cb);
    return () => this.offEmotions(cb);
  }

  /** Unregister a previously-registered onEmotions callback. */
  offEmotions(cb) {
    const i = this._emotionListeners.indexOf(cb);
    if (i !== -1) this._emotionListeners.splice(i, 1);
  }

  /**
   * Register a callback fired at the end of every render tick (after the
   * interpolated lipsync frame has been written to morphTargetInfluences).
   * The receiver gets the high-resolution `now` of the current rAF.
   * Returns an `off` function for symmetric unsubscribe.
   *
   * Used by A2FEmotionRenderer to write emotion shapes on top of lipsync
   * values without racing on the same morphTargetInfluences entry.
   */
  onAfterTick(cb) {
    if (typeof cb !== "function") return () => {};
    this._postTickListeners.push(cb);
    return () => {
      const i = this._postTickListeners.indexOf(cb);
      if (i !== -1) this._postTickListeners.splice(i, 1);
    };
  }

  /**
   * Task #20: register a one-shot-per-event callback that fires on the
   * rising edge of a queue underrun — either the queue went empty with
   * the session still open, or `renderT` advanced past the last queued
   * frame with the session still open. Used by the audio starvation
   * gate so it can pause audio on the SAME rAF the underflow is
   * detected, instead of waiting for its 30 Hz throttle (worst case
   * ~33 ms of mouth-frozen visibility).
   *
   * The latch resets the moment the driver interpolates a real frame
   * out of the queue, so a continuous multi-tick underrun is one
   * callback, not 60 per second.
   *
   * Returns an `off` function for symmetric unsubscribe.
   */
  onUnderflowDetected(cb) {
    if (typeof cb !== "function") return () => {};
    this._underflowListeners.push(cb);
    return () => {
      const i = this._underflowListeners.indexOf(cb);
      if (i !== -1) this._underflowListeners.splice(i, 1);
    };
  }

  _fireUnderflowListeners(now) {
    if (!this._underflowListeners || this._underflowListeners.length === 0) {
      return;
    }
    for (let i = 0; i < this._underflowListeners.length; i++) {
      try {
        this._underflowListeners[i](now);
      } catch (e) {
        // listener errors must never break the driver tick
      }
    }
  }

  setBlendShapeControls(controls) {
    this.blendShapeControls = controls;
    this._buildNameMap();
    if (this.debug) {
      const keys = controls ? Object.keys(controls) : [];
      console.log("[A2FLipSync] setBlendShapeControls", { count: keys.length });
    }

    if (controls && Object.keys(controls).length > 0 && !this._didAutoTest) {
      this._didAutoTest = true;
      setTimeout(() => {
        if (!this._playing) this._autoTestJawOpen();
      }, 500);
    }
  }

  /**
   * ✅ UPDATED:
   * enqueueFrames(frames, { autoStart: false }) lets you buffer frames without starting playback.
   */
  enqueueFrames(frames, opts = {}) {
    if (!Array.isArray(frames) || frames.length === 0) return;

    for (let i = 0; i < frames.length; i++) {
      this._frameQueue.push(frames[i]);
    }

    const autoStart = opts.autoStart !== false; // default true
    if (autoStart && !this._playing) {
      this._startPlayback();
    } else if (this._playing) {
      // Optional drift resync
      const latestFrameT = frames[frames.length - 1].time_ms;
      const currentStreamT = this._getStreamClock();
      const drift = currentStreamT - latestFrameT;
      if (drift > this._jitterMs * 3) {
        if (this.debug) {
          console.log(
            "[A2FLipSync] Clock drift (" +
              Math.round(drift) +
              "ms), re-syncing",
          );
        }
        this._clockOriginWall = performance.now();
        this._clockOriginStream = latestFrameT;
      }
    }
  }

  /**
   * ✅ NEW:
   * Lock A2F driver time to a specific wall time (audio start moment).
   */
  syncClock(streamTimeMs, wallNowMs) {
    this._clockOriginStream = Number(streamTimeMs || 0);
    this._clockOriginWall = Number(wallNowMs || performance.now());
    this._lastTickTime = this._clockOriginWall;

    if (this.debug) {
      console.log("[A2FLipSync] syncClock()", {
        streamTimeMs: this._clockOriginStream,
        wallNowMs: this._clockOriginWall,
      });
    }
  }

  /**
   * Inject the audio-clock getter (see `_audioClockFn`). `fn()` must
   * return the current audio playhead in ms on the SAME normalized
   * timeline as the queued frames' `time_ms` (frame-0 == 0), or a
   * non-finite value / null during the pre-audio lead-in. Passing a
   * falsy value clears the injection and reverts to the wall clock.
   */
  setAudioClock(fn) {
    this._audioClockFn = typeof fn === "function" ? fn : null;
    if (this.debug) {
      console.log("[A2FLipSync] setAudioClock()", {
        installed: !!this._audioClockFn,
      });
    }
  }

  /**
   * Task #11: freeze the stream clock at "now". While paused,
   * `_getStreamClock()` returns a constant value, so `_tick()`
   * stops advancing renderT — the mouth holds its current pose
   * instead of running ahead of paused audio.
   *
   * Idempotent: a second call while already paused is a no-op
   * (we keep the original pause stamp so resumeClock returns the
   * correct accumulated pause duration).
   */
  pauseClock() {
    if (this._clockPaused) return 0;
    this._clockPausedAtWall = performance.now();
    this._clockPaused = true;
    if (this.debug) {
      console.log("[A2FLipSync] pauseClock() at wall=" +
        this._clockPausedAtWall.toFixed(1));
    }
    return this._clockPausedAtWall;
  }

  /**
   * Task #11: unfreeze the stream clock. Shifts `_clockOriginWall`
   * forward by the elapsed pause duration so the next
   * `_getStreamClock()` call returns the same value that
   * `_clockPausedAtWall` was returning during the pause — i.e. the
   * driver picks up exactly where it left off in frame-time, without
   * re-anchoring the syncClock origin or re-counting any frames.
   *
   * Returns the pause duration in ms (0 if not paused).
   */
  resumeClock() {
    if (!this._clockPaused) return 0;
    const pauseDur = performance.now() - this._clockPausedAtWall;
    this._clockOriginWall += pauseDur;
    this._lastTickTime += pauseDur;
    this._clockPaused = false;
    this._clockPausedAtWall = 0;
    if (this.debug) {
      console.log("[A2FLipSync] resumeClock() pauseDur=" +
        pauseDur.toFixed(1) + "ms");
    }
    return pauseDur;
  }

  /**
   * Task #99 (lip-lock): how far ahead of the current stream clock the
   * buffered frames reach, in ms. Positive = frames are ahead of where
   * the mouth is rendering (healthy lead); <= 0 = the queue has run dry
   * and the mouth is about to outrun its frames. Returns -Infinity when
   * there are no frames at all. Used by the engine's hold-on-underrun
   * lip-lock to decide when to hold audio and when to resume.
   */
  // Task #99 (accounting): late-frame events this turn (mouth-skip cause).
  getUnderflowCount() {
    return this._underflowCount || 0;
  }

  getFrameLeadMs() {
    const q = this._frameQueue;
    if (!q || q.length === 0) return -Infinity;
    const last = q[q.length - 1];
    const lastT =
      last && typeof last.time_ms === "number" ? last.time_ms : 0;
    return lastT - this._getStreamClock();
  }

  /**
   * Start ticking RIGHT NOW (used when audio starts).
   * Applies the first blendshape frame synchronously so lips move
   * at the exact same instant audio begins — no rAF wait.
   * Jitter starts at 0 and ramps up to _baseJitterMs over 500ms.
   */
  startPlaybackNow() {
    if (this._playing) return;

    // Task #33: a new utterance is starting — preempt any in-flight
    // graceful decay from the previous utterance so it cannot continue
    // mutating morphs we're about to drive from real frames.
    this._cancelPendingDecay();

    this._playing = true;
    this._sessionEnded = false;
    this._loggedResolution = false;
    // Task #20: fresh session → clear stale underflow latch so the
    // first drain of THIS utterance fires a new event.
    this._underflowLatched = false;
    // Task #99: fresh turn → reset the late-frame ledger.
    this._underflowCount = 0;

    // Ramp the render offset in from a small value to _baseJitterMs over
    // the first 500ms. When _baseJitterMs is 0 (Task #99 A: render offset
    // is now the real audioCtx.outputLatency, folded into the audio clock),
    // this starts and stays at 0 — the ramp condition below is `_jitterMs <
    // _baseJitterMs`, so seeding above base would otherwise leave it stuck.
    this._jitterMs = Math.min(33, this._baseJitterMs);
    this._jitterRampStartWall = performance.now();

    if (this.debug) {
      console.log("[A2FLipSync] startPlaybackNow()", {
        queuedFrames: this._frameQueue.length,
        clockOriginStream: this._clockOriginStream,
        jitterRamp: `33 → ${this._baseJitterMs}ms over ${this._jitterRampDurationMs}ms`,
      });
    }

    if (this._frameQueue.length > 0 && this.blendShapeControls) {
      const now = performance.now();
      this._lastTickTime = now;
      const dt = 16;

      if (now >= this._clockOriginWall) {
        const renderT = this._getStreamClock();
        const q = this._frameQueue;

        let frameA = null;
        if (renderT <= q[0].time_ms) {
          frameA = q[0];
        } else {
          for (let i = 0; i < q.length - 1; i++) {
            if (q[i].time_ms <= renderT && renderT < q[i + 1].time_ms) {
              frameA = q[i];
              break;
            }
          }
          if (!frameA) frameA = q[q.length - 1];
        }

        if (frameA) {
          this._applyInterpolated(frameA, frameA, 0, dt);
          if (this.debug) {
            console.log(
              "[A2FLipSync] First frame applied synchronously at t=" +
                frameA.time_ms +
                "ms",
            );
          }
        }
      } else {
        if (this.debug) {
          console.log(
            "[A2FLipSync] Skipping early first-frame apply, audio hasn't started yet. deltaMs=",
            (this._clockOriginWall - now).toFixed(1),
          );
        }
      }
    }

    this._animFrameId = requestAnimationFrame(this._boundTick);
  }

  startPlaybackAtWall(wallStartMs) {
    if (this._playing) return;

    // Task #33: see startPlaybackNow — preempt any leftover decay before we
    // arm the timer that will eventually call startPlaybackNow.
    this._cancelPendingDecay();

    if (this._startTimerId) {
      clearTimeout(this._startTimerId);
      this._startTimerId = null;
    }

    const now = performance.now();
    const delay = Math.max(0, Number(wallStartMs) - now);

    if (this.debug) {
      console.log("[A2FLipSync] startPlaybackAtWall()", {
        wallStartMs,
        now,
        delayMs: delay,
        queuedFrames: this._frameQueue.length,
      });
    }

    if (delay <= 1) {
      this.startPlaybackNow();
      return;
    }

    this._startTimerId = setTimeout(() => {
      this._startTimerId = null;
      this.startPlaybackNow();
    }, delay);
  }

  markSessionEnded() {
    this._sessionEnded = true;
    if (this.debug) {
      console.log(
        "[A2FLipSync] Session ended, queue:",
        this._frameQueue.length,
      );
    }
  }

  _startPlayback() {
    if (this._playing) return;

    // Task #33: preempt any in-flight graceful decay from a previous
    // utterance. Without this, the decay rAF would keep clobbering
    // morph values that this fresh utterance is about to drive.
    this._cancelPendingDecay();

    this._playing = true;
    this._sessionEnded = false;
    this._loggedResolution = false;
    // Task #20: see startPlaybackNow — same reasoning here.
    this._underflowLatched = false;
    // Task #99: fresh turn → reset the late-frame ledger.
    this._underflowCount = 0;

    const firstT =
      this._frameQueue.length > 0 ? this._frameQueue[0].time_ms : 0;
    this._clockOriginWall = performance.now();
    this._clockOriginStream = firstT;
    this._lastTickTime = this._clockOriginWall;

    if (this.debug) {
      console.log("[A2FLipSync] Playback started", {
        frames: this._frameQueue.length,
        firstT,
        jitterMs: this._jitterMs,
      });
    }

    this._animFrameId = requestAnimationFrame(this._boundTick);
  }

  _getStreamClock() {
    // Audio-clock cursor (preferred): read the playhead straight from
    // the AudioContext driving the speaker. This value and every queued
    // frame's `time_ms` are indexed off the same PCM byte stream, so a
    // frame renders exactly when its audio is heard — no wall-clock
    // drift over long replies, and the cursor stalls with the audio
    // when a background tab throttles rAF. The getter returns null
    // during the pre-audio lead-in, so we fall through to the wall clock
    // until playback actually starts (and whenever no getter is set).
    if (this._audioClockFn && !this._clockPaused) {
      const t = this._audioClockFn();
      if (typeof t === "number" && Number.isFinite(t)) {
        return t;
      }
    }
    // Task #11: while paused, freeze the stream clock at the moment
    // the pause began so the render loop stops advancing through
    // frames. `resumeClock()` bumps `_clockOriginWall` by the pause
    // duration so this returns the same value it would have without
    // the pause once we come back.
    const wallNow = this._clockPaused
      ? this._clockPausedAtWall
      : performance.now();
    return this._clockOriginStream + (wallNow - this._clockOriginWall);
  }

  _tick() {
    if (!this._playing || !this.blendShapeControls) {
      this._playing = false;
      return;
    }

    const now = performance.now();
    const rawDt = now - this._lastTickTime;
    this._lastTickTime = now;
    const dt = Math.max(1, Math.min(100, rawDt));

    if (this._jitterRampStartWall > 0 && this._jitterMs < this._baseJitterMs) {
      const elapsed = now - this._jitterRampStartWall;
      const t = Math.min(1, elapsed / this._jitterRampDurationMs);
      this._jitterMs = t * this._baseJitterMs;
      if (t >= 1) this._jitterRampStartWall = 0;
    }

    const renderT = this._getStreamClock() - this._jitterMs;

    // Task #99 (B): fire any emotion keyframe the audio cursor has reached,
    // BEFORE the frame branches below — so the expression overlay tracks the
    // SAME cursor as the lips even on empty-queue (underflow) ticks.
    this._dispatchScheduledEmotions(renderT);

    const q = this._frameQueue;

    if (q.length === 0) {
      if (this._sessionEnded) {
        if (this.debug)
          console.log("[A2FLipSync] Queue empty + session ended, decaying");
        this._playing = false;
        this._animFrameId = null;
        this._decayToZero();
        return;
      }
      // Underflow: driver wanted a frame but the queue is dry and
      // the session is still open. Counted on the rising edge so a
      // multi-tick underrun is one event, not 60 events/second.
      this._a2fDebugRecordUnderflowEvent(now);
      // Task #20: fire the underflow callback on the rising edge so
      // the starvation gate can pause audio without waiting for its
      // 30 Hz throttle.
      if (!this._underflowLatched) {
        this._underflowLatched = true;
        this._underflowCount++;
        this._fireUnderflowListeners(now);
      }
      // Keep the 250 ms sample cadence running across underruns —
      // the rising-edge dedup above gives us the EVENT count, but
      // every empty-queue tick is itself an evidence point worth
      // capturing (frameError, queue stats, blendshape state stuck
      // on the last frame). The helper short-circuits below the
      // 250 ms throttle, so this is one branch-and-skip per rAF.
      this._a2fDebugMaybeRecordRenderSample(
        null, null, null, null, renderT, false, now,
      );
      this._firePostTickListeners(now);
      this._animFrameId = requestAnimationFrame(this._boundTick);
      return;
    }

    // We have at least one frame — clear the "in underflow" latch so
    // a future drain counts as a new event. Gated on `isRecording`
    // (a) to honour the "every hook gates" contract, and (b) so the
    // hot path stays branch-and-skip when the debug flag is off.
    if (this._a2fDebugRecorder && this._a2fDebugRecorder.isRecording) {
      this._a2fDebugWasInUnderflow = false;
    }
    // Task #20: clear the underflow latch as soon as we have a frame
    // to interpolate AND we won't fall into the reused-old-frame
    // branch below. We re-check after the branch selection so a
    // "past last frame" tick doesn't reset the latch.

    let frameA = null;
    let frameB = null;
    let alpha = 0;
    let reusedOldFrame = false;

    if (renderT <= q[0].time_ms) {
      frameA = q[0];
      frameB = q[0];
      alpha = 0;
    } else if (renderT >= q[q.length - 1].time_ms) {
      frameA = q[q.length - 1];
      frameB = q[q.length - 1];
      alpha = 0;
      reusedOldFrame = true;

      // Task #20: past-last-frame WITH session still open is the OTHER
      // form of underrun — the queue has frames but they're all
      // historical. Fire the underflow callback on the rising edge so
      // the starvation gate can pause audio immediately.
      if (!this._sessionEnded && !this._underflowLatched) {
        this._underflowLatched = true;
        this._underflowCount++;
        this._fireUnderflowListeners(now);
      }

      if (this._sessionEnded) {
        this._applyInterpolated(frameA, frameB, 0, dt);
        // Sample once before tear-down so the final state is
        // captured in the JSON.
        this._a2fDebugMaybeRecordRenderSample(
          frameA, null, null, null, renderT, true, now,
        );
        if (this.debug)
          console.log("[A2FLipSync] Past last frame + session ended, decaying");
        this._playing = false;
        this._animFrameId = null;
        this._frameQueue = [];
        this._emotionQueue = [];
        this._lastFiredEmotionTimeMs = -Infinity;
        this._decayToZero();
        return;
      }
    } else {
      for (let i = 0; i < q.length - 1; i++) {
        if (q[i].time_ms <= renderT && renderT < q[i + 1].time_ms) {
          frameA = q[i];
          frameB = q[i + 1];
          const span = frameB.time_ms - frameA.time_ms;
          alpha = span > 0 ? (renderT - frameA.time_ms) / span : 0;
          alpha = Math.max(0, Math.min(1, alpha));
          break;
        }
      }
    }

    if (frameA && frameB) {
      this._applyInterpolated(frameA, frameB, alpha, dt);

      // Task #20: a real interpolated frame (not a reuse of the
      // tail) means the queue is healthy again — clear the rising-
      // edge latch so the NEXT drain or past-last-frame state fires
      // a fresh `onUnderflowDetected` event. Without this reset the
      // callback would only ever fire once per driver lifetime and
      // every subsequent underrun would have to wait for the 33 ms
      // throttled `onAfterTick` evaluator.
      if (!reusedOldFrame && this._underflowLatched) {
        this._underflowLatched = false;
      }

      // Compute the effective applied-frame time for the recorder:
      // when interpolating it's `renderT` (the point in stream-time
      // we are visualizing); in the start / reuse branches it's
      // `frameA.time_ms` exactly. `appliedFrameTimeMs` drives the
      // `frameErrorMs` metric the recorder uses for warnings.
      const interpolating = frameA !== frameB;
      const appliedFrame = interpolating ? { time_ms: renderT } : frameA;

      // prev / next: frame just before and after the applied frame
      // in the queue. For interpolation, prev = frameA and next =
      // frameB; for the boundary branches, look up neighbours.
      let prevFrame = null;
      let nextFrame = null;
      if (interpolating) {
        prevFrame = frameA;
        nextFrame = frameB;
      } else {
        const idxA = q.indexOf(frameA);
        if (idxA > 0) prevFrame = q[idxA - 1];
        if (idxA >= 0 && idxA < q.length - 1) nextFrame = q[idxA + 1];
      }

      this._a2fDebugMaybeRecordRenderSample(
        appliedFrame,
        prevFrame,
        nextFrame,
        interpolating ? alpha : null,
        renderT,
        reusedOldFrame,
        now,
      );
    }

    this._pruneOldFrames(renderT);

    this._firePostTickListeners(now);

    this._animFrameId = requestAnimationFrame(this._boundTick);
  }

  /**
   * Fire all post-tick listeners (registered via onAfterTick). Wrapped in
   * try/catch so a misbehaving listener never crashes the render loop.
   */
  _firePostTickListeners(now) {
    if (!this._postTickListeners || this._postTickListeners.length === 0) {
      return;
    }
    for (let i = 0; i < this._postTickListeners.length; i++) {
      try {
        this._postTickListeners[i](now);
      } catch (_) {}
    }
  }

  _pruneOldFrames(renderT) {
    const cutoff = renderT - this._pruneMarginMs;
    // Capture the gate ONCE per call so the loop body stays branch-
    // and-store. The recorder check honours the "every debug hook
    // gates on isRecording" contract; the try/catch is required by
    // the same contract — a recorder bug must never break pruning.
    const debugOn =
      this._a2fDebugRecorder && this._a2fDebugRecorder.isRecording;
    while (
      this._frameQueue.length > 2 &&
      this._frameQueue[0].time_ms < cutoff
    ) {
      this._frameQueue.shift();
      if (debugOn) {
        try {
          // Frame fell behind the prune cutoff (~500 ms past renderT)
          // without being selected for display this tick. Per Task #27
          // this counts as a dropped frame for the per-utterance
          // summary — the only frame-loss surface the driver owns.
          this._a2fDebugDroppedFrameCount++;
        } catch (_) {}
      }
    }
  }

  _applyInterpolated(frameA, frameB, alpha, dtMs) {
    // Single chokepoint that writes morphTargetInfluences. Stamp the
    // shared "A2F is animating" wall clock here so isAnyActive() tracks
    // every applied frame from the rAF-driven queue path.
    const _nowMs =
      typeof performance !== "undefined" && performance.now
        ? performance.now()
        : Date.now();
    this._lastFrameApplyWall = _nowMs;
    A2FLipSyncDriver._lastFrameWall = _nowMs;

    // Task #99 (A): asymmetric low-pass. dtMs is constant for the whole
    // tick, so precompute both directional smoothing factors once; pick
    // per-shape below from the sign of (target - prev).
    const betaAttack = 1 - Math.exp(-dtMs / this._smoothAttackTauMs);
    const betaRelease = 1 - Math.exp(-dtMs / this._smoothReleaseTauMs);

    const bsA = frameA.blendshapes || {};
    const bsB = frameB.blendshapes || {};

    const allNames = new Set();
    for (const n in bsA) allNames.add(n);
    for (const n in bsB) allNames.add(n);

    let resolvedCount = 0;
    let unresolvedCount = 0;

    // Task #27: When recording, snapshot raw + smoothed values for
    // the spec's six lipsync shapes so `_a2fDebugMaybeRecordRenderSample`
    // can stamp them on the next render-sample entry. Built fresh each
    // call so a frame missing a shape doesn't carry stale values.
    let dbgSnap = null;
    if (this._a2fDebugRecorder && this._a2fDebugRecorder.isRecording) {
      dbgSnap = { raw: {}, smoothed: {} };
      this._a2fDebugLastAppliedSnapshot = dbgSnap;
    }

    for (const name of allNames) {
      const resolved = this._resolve(name);
      if (!resolved) {
        unresolvedCount++;
        continue;
      }

      resolvedCount++;

      const ctl = this.blendShapeControls[resolved];
      if (!ctl) continue;
      const firstCtl = Array.isArray(ctl) ? ctl[0] : ctl;
      if (!firstCtl || !firstCtl.mesh || typeof firstCtl.index !== "number")
        continue;

      const v0 = bsA[name] || 0;
      const v1 = bsB[name] || 0;
      const target = v0 + alpha * (v1 - v0);

      const prev = this._currentValues[resolved] || 0;
      // Rising toward more-open = attack (fast); falling toward rest =
      // release (slow). Keeps consonant onsets crisp without chatter.
      const beta = target >= prev ? betaAttack : betaRelease;
      const smoothed = prev + beta * (target - prev);
      this._currentValues[resolved] = smoothed;

      const clamped = Math.max(0, Math.min(1, smoothed));
      this._setControlValue(resolved, clamped);

      if (dbgSnap) {
        // Look up by lowercase bridge name so common rig spellings
        // (jawOpen / JawOpen / jawopen) all map to the canonical key.
        const lc = String(name).toLowerCase();
        if (_A2F_DEBUG_TRACKED_SHAPES.has(lc)) {
          const canon = _A2F_DEBUG_SHAPE_CANON[lc];
          dbgSnap.raw[canon] = target;
          dbgSnap.smoothed[canon] = smoothed;
        }
      }
    }

    if (this.debug && !this._loggedResolution) {
      console.log(
        `[A2FLipSync] Frame resolution: ${resolvedCount} matched, ${unresolvedCount} unmatched`,
      );
      const unmatched = [];
      for (const n of allNames) {
        if (!this._resolve(n)) unmatched.push(n);
        if (unmatched.length >= 15) break;
      }
      if (unmatched.length > 0) {
        console.log("[A2FLipSync] Unmatched sample:", unmatched);
      }
      this._loggedResolution = true;
    }

  }

  _decayToZero() {
    if (!this.blendShapeControls) return;

    const steps = 10;
    let step = 0;

    const decay = () => {
      step++;
      const factor = 1 - step / steps;

      for (const name in this._currentValues) {
        const val = Math.max(0, this._currentValues[name] * factor);
        this._setControlValue(name, val);
      }

      if (step < steps) requestAnimationFrame(decay);
      else {
        this._currentValues = {};
        this.zeroAll();
      }
    };

    requestAnimationFrame(decay);
  }

  zeroAll() {
    if (!this.blendShapeControls) return;
    for (const key of Object.keys(this.blendShapeControls)) {
      this._applyControl(this.blendShapeControls[key], 0);
    }
    this._currentValues = {};
  }

  /**
   * Task #33: cancel any in-flight flushAndDecay rAF and bump the generation
   * token so the captured `myGen` inside the decay closure goes stale on its
   * next tick. Used by `stop()` and by all new-turn entry points
   * (`startPlaybackNow`, `startPlaybackAtWall`, `_startPlayback`) to ensure
   * an old decay can never clobber a fresh utterance's morphs.
   */
  _cancelPendingDecay() {
    if (this._flushAndDecayRafId) {
      cancelAnimationFrame(this._flushAndDecayRafId);
      this._flushAndDecayRafId = null;
    }
    this._flushAndDecayGen = (this._flushAndDecayGen || 0) + 1;
    this._flushAndDecayActive = false;
  }

  stop() {
    if (this._startTimerId) {
      clearTimeout(this._startTimerId);
      this._startTimerId = null;
    }
    this._playing = false;
    if (this._animFrameId) cancelAnimationFrame(this._animFrameId);
    this._animFrameId = null;
    // Task #33: cancel any in-flight flushAndDecay so it cannot continue
    // mutating morphs after stop() returns.
    this._cancelPendingDecay();
    this._frameQueue = [];
    this._emotionQueue = [];
    this._lastFiredEmotionTimeMs = -Infinity;
    this._currentValues = {};
    this._loggedResolution = false;
    this._jitterMs = this._baseJitterMs;
    this._jitterRampStartWall = 0;
    if (this.debug) console.log("[A2FLipSync] Playback stopped");
  }

  reset() {
    this.stop();
    this.zeroAll();
  }

  /**
   * Task #33: graceful end-of-utterance teardown.
   *
   * The hard `reset()` above is correct for cancels/interrupts (drop everything
   * NOW), but at natural end-of-audio it caused two bugs documented in the
   * Task #33 debug captures:
   *   1. The rAF `_tick` loop is killed before `_decayToZero` can run, so
   *      jaw/mouth shapes freeze on whatever value the last applied frame
   *      left them at — typically wide-open mid-vowel.
   *   2. Any frames still sitting in `_frameQueue` (commonly ~18, the audio
   *      ahead-of-frame slack) are abandoned — the very last bit of mouth
   *      motion never plays.
   *
   * This method addresses both:
   *   - If `_frameQueue` is non-empty, snap the morphs forward by applying
   *     the LAST queued frame once. That collapses the abandoned tail into
   *     a single catch-up render so the decay starts from the true final
   *     pose, not from a stale mid-utterance one.
   *   - Then run a linear rAF decay on `_currentValues` over `decayMs` so
   *     every shape we wrote (lipsync shapes plus any face shapes a frame
   *     touched) eases back to 0 instead of snapping. `lipSyncShapes` is
   *     accepted purely for documentation — every key in `_currentValues`
   *     decays uniformly so we never leave a stray non-zero value behind.
   *   - On natural completion, calls `zeroAll()` and inlines the small
   *     subset of `stop()` it actually needs (clears `_playing`,
   *     `_loggedResolution`, jitter state). It intentionally does NOT call
   *     `stop()`, because (a) `_frameQueue` has already been emptied above
   *     and `_currentValues` is now zero by construction, and (b) calling
   *     `stop()` would bump the generation token redundantly.
   *
   * Idempotent. If already running a flush, subsequent calls are no-ops.
   * If `_currentValues` is empty (driver never wrote a frame this turn),
   * collapses to a synchronous `zeroAll()` + `stop()`.
   *
   * Preemption: if `_cancelPendingDecay()` fires (called by `stop()` and by
   * all new-turn entry points), the generation token bumps and the captured
   * `myGen` inside the tick closure goes stale — the next rAF tick bails
   * before mutating any morphs, so a fresh utterance can never be clobbered
   * by an in-flight decay from the previous utterance.
   */
  flushAndDecay({ lipSyncShapes, decayMs } = {}) {
    void lipSyncShapes; // intentionally unused; documented above
    const totalMs = Number.isFinite(decayMs) && decayMs > 0 ? decayMs : 400;

    if (this._flushAndDecayActive) return;

    // Mark the session ended so any future enqueueFrames is a no-op and so
    // `_tick` (if it somehow runs once more) takes the ended-branch.
    this._sessionEnded = true;

    // Stop the normal tick loop — we are about to drive the morphs ourselves
    // for the rest of this turn.
    if (this._animFrameId) {
      cancelAnimationFrame(this._animFrameId);
      this._animFrameId = null;
    }
    this._playing = false;

    // Catch-up: collapse the abandoned-frame tail into the final pose so the
    // decay below starts from where the utterance actually ended, not where
    // the audio happened to stop pulling frames.
    const q = this._frameQueue;
    if (q && q.length > 0 && this.blendShapeControls) {
      const last = q[q.length - 1];
      try {
        // dt of one frame interval keeps the smoothing filter responsive
        // without overshooting; we only call this once.
        this._applyInterpolated(last, last, 0, 16);
      } catch (_) {}
    }
    this._frameQueue = [];
    this._emotionQueue = [];
    this._lastFiredEmotionTimeMs = -Infinity;

    // Snapshot the values to decay from. If nothing was ever written, just
    // zero & stop synchronously — no need to spin up an rAF loop for that.
    const startValues = { ...(this._currentValues || {}) };
    if (Object.keys(startValues).length === 0 || !this.blendShapeControls) {
      this.zeroAll();
      this.stop();
      return;
    }

    // Generation token: if `stop()` (or any path that bumps it) runs while
    // this decay is in flight, the captured `myGen` will go stale and the
    // tick will bail before mutating morphs that now belong to a fresh
    // utterance. `_flushAndDecayActive` is the reentry guard; the gen token
    // is the preemption guard.
    this._flushAndDecayGen = (this._flushAndDecayGen || 0) + 1;
    const myGen = this._flushAndDecayGen;
    this._flushAndDecayActive = true;

    const startWall = (typeof performance !== "undefined" && performance.now)
      ? performance.now()
      : Date.now();

    const tick = () => {
      this._flushAndDecayRafId = null;

      // Preemption: a new utterance (or a hard reset) bumped the gen token.
      // Drop this tick on the floor — do NOT touch morphs, do NOT call stop().
      if (myGen !== this._flushAndDecayGen) return;

      const nowWall = (typeof performance !== "undefined" && performance.now)
        ? performance.now()
        : Date.now();
      const t = Math.min(1, (nowWall - startWall) / totalMs);
      const factor = 1 - t;

      for (const name in startValues) {
        const target = Math.max(0, startValues[name] * factor);
        try { this._setControlValue(name, target); } catch (_) {}
        // Keep `_currentValues` mirroring what's actually on the morphs so
        // any caller that peeks at it during decay sees the truth.
        this._currentValues[name] = target;
        // Also stamp the active-driver wall so isAnyActive() keeps reporting
        // true through the decay tail and idle controllers don't yank the
        // eyes mid-fade.
        this._lastFrameApplyWall = nowWall;
        A2FLipSyncDriver._lastFrameWall = nowWall;
      }

      if (t < 1) {
        this._flushAndDecayRafId = requestAnimationFrame(tick);
      } else {
        // Natural completion. Zero the morphs we wrote, but DON'T call
        // `this.stop()` — stop() bumps the gen token (harmless here) and
        // also wipes `_currentValues` / `_frameQueue` which we already
        // own. Inline the small subset of stop() we actually need so we
        // don't fight any future code that grows stop()'s responsibilities.
        this._flushAndDecayActive = false;
        this.zeroAll();
        this._playing = false;
        this._loggedResolution = false;
        this._jitterMs = this._baseJitterMs;
        this._jitterRampStartWall = 0;
        if (this.debug) console.log("[A2FLipSync] flushAndDecay complete");
      }
    };

    this._flushAndDecayRafId = requestAnimationFrame(tick);
  }

  /**
   * Permanently disconnect this driver from the active-driver registry.
   * `stop()` is reused across utterances (and we want isAnyActive() to keep
   * reporting true through the brief decay-to-zero tail), so the registry
   * entry is only removed on `dispose()` — typically when the model is
   * swapped or the page tears down. Safe to call multiple times.
   */
  dispose() {
    this.stop();
    A2FLipSyncDriver._activeDrivers.delete(this);
  }

  /**
   * Class-level "is A2F currently animating?" probe used by the local
   * idle controllers (BlinkController, EyeTrackingController) so they
   * don't fight A2F's eye blendshapes frame-by-frame. Returns true if any
   * registered driver instance has applied a frame within the last
   * `graceMs` milliseconds — every applied frame flows through
   * `_applyInterpolated`. The grace window also keeps the handoff smooth
   * across the brief `_decayToZero` tail after `_playing` flips false.
   */
  static isAnyActive(graceMs = 300) {
    if (!A2FLipSyncDriver._activeDrivers ||
        A2FLipSyncDriver._activeDrivers.size === 0) {
      return false;
    }
    // Guard the startup edge: until the first frame has been applied,
    // _lastFrameWall is 0 and `nowMs - 0` would briefly look "fresh"
    // during the first ~300 ms of page load. Treat "no frame ever
    // applied" as not-active so idle controllers run normally before
    // any speech.
    if (A2FLipSyncDriver._lastFrameWall <= 0) return false;
    const nowMs =
      typeof performance !== "undefined" && performance.now
        ? performance.now()
        : Date.now();
    return nowMs - A2FLipSyncDriver._lastFrameWall < graceMs;
  }

  // ===== Task #27 — debug-recorder render-loop sampling =====

  /**
   * Inject the recorder + audio-clock snapshot fn from the wiring
   * layer. Called once after construction by RealtimeLLMToElevenLabs.
   *
   * @param {object} ctx
   * @param {object} ctx.recorder       — A2FDebugRecorder instance.
   * @param {Function} ctx.getDebugSnapshot — () => {
   *   currentAudioTimeMs: number|null,
   *   audioContextCurrentTime: number|null,
   *   playbackStartTime: number|null,
   * } sampled at call time. Must NOT throw.
   */
  setDebugContext(ctx) {
    if (!ctx || typeof ctx !== "object") return;
    this._a2fDebugRecorder = ctx.recorder || null;
    this._a2fDebugSnapshotFn =
      typeof ctx.getDebugSnapshot === "function"
        ? ctx.getDebugSnapshot
        : null;
  }

  /**
   * Reset all per-utterance render-loop counters / rolling buffers.
   * Called from the wiring layer's `_a2fDebugBeginUtterance` right
   * after `recorder.beginUtterance(...)` so the state lines up with
   * the new recording session. Safe to call when the recorder is
   * absent — it just zeroes local state.
   */
  _a2fDebugBeginRenderUtterance() {
    this._a2fDebugRenderSampleIndex = 0;
    this._a2fDebugLastRenderSampleAtMonoMs = 0;
    this._a2fDebugDroppedFrameCount = 0;
    this._a2fDebugUnderflowCount = 0;
    this._a2fDebugWasInUnderflow = false;
    this._a2fDebugSustainedErrStartMs = 0;
    this._a2fDebugRollingErrs = [];
    this._a2fDebugLastRawJawCrossingMs = 0;
    this._a2fDebugLastSmoothedJawCrossingMs = 0;
    this._a2fDebugPrevRawJawAbove = false;
    this._a2fDebugPrevSmoothedJawAbove = false;
    this._a2fDebugLastAppliedSnapshot = null;
  }

  /**
   * Called from `_tick` immediately after `_applyInterpolated` runs
   * (or after the empty-queue underflow branch). Throttles itself to
   * one record every 250 ms; cheap branch-and-return when off-flag,
   * mid-throttle, or no audio clock yet.
   *
   * @param {object|null} appliedFrame — frameA when in the start /
   *   reuse branches, or the synthetic interpolation midpoint object
   *   {time_ms} when interpolating. null on underflow ticks.
   * @param {object|null} prevFrame — q[i-1] or null.
   * @param {object|null} nextFrame — q[i+1] or null.
   * @param {number|null} alpha — interpolation alpha, or null.
   * @param {number} renderT — driver stream-clock renderT.
   * @param {boolean} reusedOldFrame — driver fell off the end of the queue.
   * @param {number} nowMs — performance.now() captured by the caller.
   */
  _a2fDebugMaybeRecordRenderSample(
    appliedFrame,
    prevFrame,
    nextFrame,
    alpha,
    renderT,
    reusedOldFrame,
    nowMs,
  ) {
    const rec = this._a2fDebugRecorder;
    if (!rec || !rec.isRecording) return;
    if (
      nowMs - this._a2fDebugLastRenderSampleAtMonoMs <
      _A2F_DEBUG_RENDER_SAMPLE_INTERVAL_MS
    ) {
      return;
    }
    try {
      const snap = this._a2fDebugSnapshotFn
        ? this._a2fDebugSnapshotFn()
        : null;
      const currentAudioTimeMs =
        snap && Number.isFinite(snap.currentAudioTimeMs)
          ? snap.currentAudioTimeMs
          : null;
      // Skip pre-audio-start window — frameError isn't meaningful
      // until the master audio clock is running. Don't bump the
      // throttle clock, so the first post-start tick records.
      if (currentAudioTimeMs == null) return;

      this._a2fDebugLastRenderSampleAtMonoMs = nowMs;
      const idx = this._a2fDebugRenderSampleIndex++;

      const appliedFrameTimeMs =
        appliedFrame && Number.isFinite(appliedFrame.time_ms)
          ? appliedFrame.time_ms
          : null;
      const frameErrorMs =
        appliedFrameTimeMs != null
          ? appliedFrameTimeMs - currentAudioTimeMs
          : null;

      const q = this._frameQueue;
      const earliest =
        q.length > 0 && Number.isFinite(q[0].time_ms) ? q[0].time_ms : null;
      const latest =
        q.length > 0 && Number.isFinite(q[q.length - 1].time_ms)
          ? q[q.length - 1].time_ms
          : null;
      const frameQueueLeadMs =
        latest != null ? latest - currentAudioTimeMs : null;

      // Branch flags. `appliedFutureFrame` = the frame we displayed is
      // still in the future relative to the master audio clock by more
      // than the standard 50 ms tolerance. `appliedLateFrame` = the
      // frame we displayed is more than 50 ms behind the audio clock
      // (bad — we're playing a stale frame). `reusedOldFrame` is set
      // by the caller (the "renderT >= last frame" branch).
      const appliedFutureFrame =
        frameErrorMs != null && frameErrorMs > 50;
      const appliedLateFrame = frameErrorMs != null && frameErrorMs < -50;

      const dbg = this._a2fDebugLastAppliedSnapshot;
      const rawBlendshapeValues =
        dbg && dbg.raw ? Object.assign({}, dbg.raw) : {};
      const smoothedBlendshapeValues =
        dbg && dbg.smoothed ? Object.assign({}, dbg.smoothed) : {};

      rec.addRenderSample({
        renderSampleIndex: idx,
        sampledAtMonoMs: nowMs,
        audioContextCurrentTime:
          snap && Number.isFinite(snap.audioContextCurrentTime)
            ? snap.audioContextCurrentTime
            : null,
        playbackStartTime:
          snap && Number.isFinite(snap.playbackStartTime)
            ? snap.playbackStartTime
            : null,
        currentAudioTimeMs,
        targetFrameTimeMs: currentAudioTimeMs, // strict target ≈ audio clock
        appliedFrameTimeMs,
        frameErrorMs,
        previousFrameTimeMs:
          prevFrame && Number.isFinite(prevFrame.time_ms)
            ? prevFrame.time_ms
            : null,
        nextFrameTimeMs:
          nextFrame && Number.isFinite(nextFrame.time_ms)
            ? nextFrame.time_ms
            : null,
        interpolationAlpha: Number.isFinite(alpha) ? alpha : null,
        frameQueueLength: q.length,
        earliestFrameInQueueMs: earliest,
        latestFrameInQueueMs: latest,
        frameQueueLeadMs,
        droppedFrameCount: this._a2fDebugDroppedFrameCount,
        underflowCount: this._a2fDebugUnderflowCount,
        reusedOldFrame: !!reusedOldFrame,
        appliedFutureFrame,
        appliedLateFrame,
        smoothingTauMs: Number.isFinite(this._smoothTau)
          ? this._smoothTau
          : null,
        rawBlendshapeValues,
        smoothedBlendshapeValues,
      });

      // ----- Frame-error warnings (worst-case-wins dedup) -----
      // We use `upsertWarning` so the locked-in entry per type
      // carries the WORST sample's details (largest |frameErrorMs|,
      // longest sustained streak, longest smoothing lag) rather
      // than the first threshold crossing — matches Task #27 spec.
      if (frameErrorMs != null) {
        const absErr = Math.abs(frameErrorMs);
        if (absErr > 100) {
          rec.upsertWarning(
            "FRAME_ERROR_OVER_100",
            {
              renderSampleIndex: idx,
              frameErrorMs,
              absFrameErrorMs: absErr,
              currentAudioTimeMs,
              appliedFrameTimeMs,
            },
            (existing, cand) =>
              Number(cand.absFrameErrorMs) >
              Number(existing && existing.absFrameErrorMs),
          );
        }
        if (absErr > 50) {
          rec.upsertWarning(
            "FRAME_ERROR_OVER_50",
            {
              renderSampleIndex: idx,
              frameErrorMs,
              absFrameErrorMs: absErr,
              currentAudioTimeMs,
              appliedFrameTimeMs,
            },
            (existing, cand) =>
              Number(cand.absFrameErrorMs) >
              Number(existing && existing.absFrameErrorMs),
          );
        }

        // Sustained > 100 for > 500 ms continuously. Worst-case =
        // longest streak duration captured.
        if (absErr > 100) {
          if (this._a2fDebugSustainedErrStartMs === 0) {
            this._a2fDebugSustainedErrStartMs = nowMs;
          } else if (
            nowMs - this._a2fDebugSustainedErrStartMs >
            _A2F_DEBUG_SUSTAINED_ERR_WINDOW_MS
          ) {
            const durationMs = nowMs - this._a2fDebugSustainedErrStartMs;
            rec.upsertWarning(
              "SUSTAINED_FRAME_ERROR_OVER_100",
              {
                renderSampleIndex: idx,
                durationMs,
                frameErrorMs,
                absFrameErrorMs: absErr,
              },
              (existing, cand) =>
                Number(cand.durationMs) >
                Number(existing && existing.durationMs),
            );
          }
        } else {
          this._a2fDebugSustainedErrStartMs = 0;
        }

        // Rolling avgAbsErr buffer for the smoothing-lag heuristic.
        this._a2fDebugRollingErrs.push({ atMs: nowMs, absErr });
        while (
          this._a2fDebugRollingErrs.length > _A2F_DEBUG_ROLLING_WINDOW
        ) {
          this._a2fDebugRollingErrs.shift();
        }
      }

      // ----- Smoothing-lag heuristic (SMOOTHING_MAY_BE_TOO_SLOW) -----
      // Track jawOpen raw and smoothed crossings over the open
      // threshold; if the smoothed crossing lags the raw one by more
      // than 2 * tau while the average frame error is small, the
      // smoothing filter itself (not the timeline) is the suspect.
      const rawJaw = Number(rawBlendshapeValues.jawOpen);
      const smoothedJaw = Number(smoothedBlendshapeValues.jawOpen);
      if (Number.isFinite(rawJaw) && Number.isFinite(smoothedJaw)) {
        const rawAbove = rawJaw > _A2F_DEBUG_JAW_THRESHOLD;
        const smoothedAbove = smoothedJaw > _A2F_DEBUG_JAW_THRESHOLD;
        if (rawAbove !== this._a2fDebugPrevRawJawAbove) {
          this._a2fDebugLastRawJawCrossingMs = nowMs;
        }
        if (smoothedAbove !== this._a2fDebugPrevSmoothedJawAbove) {
          this._a2fDebugLastSmoothedJawCrossingMs = nowMs;
        }
        this._a2fDebugPrevRawJawAbove = rawAbove;
        this._a2fDebugPrevSmoothedJawAbove = smoothedAbove;

        if (
          Number.isFinite(this._smoothTau) &&
          this._a2fDebugLastRawJawCrossingMs > 0 &&
          this._a2fDebugLastSmoothedJawCrossingMs >
            this._a2fDebugLastRawJawCrossingMs
        ) {
          const lagMs =
            this._a2fDebugLastSmoothedJawCrossingMs -
            this._a2fDebugLastRawJawCrossingMs;
          // Average abs error over the rolling window.
          let sumAbs = 0;
          for (const e of this._a2fDebugRollingErrs) sumAbs += e.absErr;
          const avgAbs =
            this._a2fDebugRollingErrs.length > 0
              ? sumAbs / this._a2fDebugRollingErrs.length
              : 0;
          if (lagMs > this._smoothTau * 2 && avgAbs < 33) {
            rec.upsertWarning(
              "SMOOTHING_MAY_BE_TOO_SLOW",
              {
                renderSampleIndex: idx,
                smoothingVisualDelayEstimateMs: lagMs,
                smoothingTauMs: this._smoothTau,
                avgAbsFrameErrorMs: avgAbs,
              },
              (existing, cand) =>
                Number(cand.smoothingVisualDelayEstimateMs) >
                Number(existing && existing.smoothingVisualDelayEstimateMs),
            );
          }
        }
      }
    } catch (e) {
      // Recorder bugs must never break the lipsync hot path.
      try {
        console.warn("[A2FDebug] render-sample hook failed:", e);
      } catch (_) {}
    }
  }

  /**
   * Underflow event — driver wanted to render a frame but the queue
   * was empty AND the session isn't done. Counted on the rising edge
   * (transition from "had frames" to "no frames") to avoid 60 Hz
   * spam during a real underrun. Recorder-level dedup means
   * `BROWSER_RENDER_UNDERFLOW` only fires once per session, but the
   * counter keeps incrementing so the summary's `underflowCount`
   * reflects the true number of underrun *events* in the run.
   */
  _a2fDebugRecordUnderflowEvent(nowMs) {
    // Cheap off-flag short-circuit: when the recorder is absent or
    // the debug flag is off we touch nothing — debug-state mutation
    // gated per the "every hook gates on isRecording" contract.
    const rec = this._a2fDebugRecorder;
    if (!rec || !rec.isRecording) return;
    if (this._a2fDebugWasInUnderflow) return;
    try {
      this._a2fDebugWasInUnderflow = true;
      this._a2fDebugUnderflowCount++;
      let atAudioTimeMs = null;
      if (this._a2fDebugSnapshotFn) {
        const snap = this._a2fDebugSnapshotFn();
        if (snap && Number.isFinite(snap.currentAudioTimeMs)) {
          atAudioTimeMs = snap.currentAudioTimeMs;
        }
      }
      // Worst-case = highest underflowCount seen, so the locked-in
      // entry always reflects the most-impactful underrun for the
      // utterance (matches Task #27 worst-case-wins semantics).
      rec.upsertWarning(
        "BROWSER_RENDER_UNDERFLOW",
        {
          atAudioTimeMs,
          underflowCount: this._a2fDebugUnderflowCount,
          sampledAtMonoMs: nowMs,
        },
        (existing, cand) =>
          Number(cand.underflowCount) >
          Number(existing && existing.underflowCount),
      );
    } catch (_) {}
  }

  // ===== AUTO TEST (unchanged) =====
  _autoTestJawOpen() {
    const holdMs = 1000;
    const key = this._resolve("JawOpen") || "JawOpen";
    const rawCtl = this.blendShapeControls
      ? this.blendShapeControls[key]
      : null;
    const firstCtl = Array.isArray(rawCtl) ? rawCtl[0] : rawCtl;

    if (!firstCtl || !firstCtl.mesh || typeof firstCtl.index !== "number") {
      console.warn(
        "[A2FLipSync] AUTO-TEST: JawOpen not found. Keys sample:",
        Object.keys(this.blendShapeControls || {}).slice(0, 25),
      );
      return;
    }

    const mirrors = this._mirrorMap[key] || [];
    const allKeys = [key, ...mirrors];

    console.log(
      `%c[A2FLipSync] AUTO-TEST: JawOpen=100% on ${allKeys.length} variant(s): [${allKeys.join(
        ", ",
      )}] — hold ${holdMs}ms`,
      "color:#00ff00;font-weight:bold;font-size:14px",
    );

    for (const k of allKeys) {
      const raw = this.blendShapeControls?.[k];
      const c = Array.isArray(raw) ? raw[0] : raw;
      const mesh = c?.mesh;
      const dict = mesh?.morphTargetDictionary;
      const fullIdx = dict ? dict[k] : undefined;
      let fullIdxCI = undefined;
      if (dict && fullIdx === undefined) {
        const found = Object.keys(dict).find(
          (dk) => dk.toLowerCase() === k.toLowerCase(),
        );
        if (found) fullIdxCI = dict[found];
      }
      console.log(
        `%c[A2FLipSync] AUTO-TEST DIAG: ${k}`,
        "color:#00ccff;font-weight:bold",
        {
          meshName: mesh?.name ?? "NO MESH",
          ctlIndex: c?.index,
          influencesLen: mesh?.morphTargetInfluences?.length ?? 0,
          dictHasFullKey: fullIdx !== undefined,
          dictIndexFull: fullIdx,
          dictIndexFullCI: fullIdxCI,
          indexMatchFull: fullIdx !== undefined ? fullIdx === c?.index : null,
          indexMatchFullCI:
            fullIdxCI !== undefined ? fullIdxCI === c?.index : null,
          isArray: Array.isArray(raw),
          targetCount: Array.isArray(raw) ? raw.length : 1,
        },
      );
    }

    this._setControlValue(key, 0.0);

    setTimeout(() => {
      this._setControlValue(key, 0);
      console.log(
        `%c[A2FLipSync] AUTO-TEST: JawOpen reset 0% on ${allKeys.length} variant(s)`,
        "color:#ffaa00;font-weight:bold;font-size:14px",
      );
    }, holdMs);
  }
}

A2FLipSyncDriver._activeDrivers = new Set();
A2FLipSyncDriver._lastFrameWall = 0;
